import { useState, useEffect, useCallback } from "react";
import { useI18n } from "./i18n";
import type { GameContent } from "@bastion/data";

const STORAGE_KEY = "bastion-selected-map";
const DEFAULT_MAP_ID = "frontier_endless";
const AVAILABLE_MAP_IDS = new Set([DEFAULT_MAP_ID]);

/** 将传入地图 ID 限制为玩家可用地图。 */
export function getAvailableMapId(mapId: string | null | undefined): string {
  return mapId && AVAILABLE_MAP_IDS.has(mapId) ? mapId : DEFAULT_MAP_ID;
}

/** 地图选择只向玩家开放无尽边疆；旧值或无效值会回退并写回默认值。 */
export function getStoredMapId(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const mapId = getAvailableMapId(stored);
    if (stored !== mapId) localStorage.setItem(STORAGE_KEY, mapId);
    return mapId;
  } catch {
    // 存储不可用时仍使用唯一开放地图，并尽力修复存储值。
    saveMapId(DEFAULT_MAP_ID);
    return DEFAULT_MAP_ID;
  }
}

/** 仅返回面向玩家开放的地图，不修改底层内容数据。 */
export function filterAvailableMaps<T extends { id: string }>(
  maps: Record<string, T>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(maps).filter(([, map]) => AVAILABLE_MAP_IDS.has(map.id)),
  );
}

/** 将地图选择持久化到 localStorage。 */
export function saveMapId(mapId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, mapId);
  } catch {
    // 存储不可用——静默忽略。
  }
}

interface MapInfo {
  id: string;
  name: string;
  totalWaves: number;
  /** 是否为无尽模式地图 */
  isEndless: boolean;
  /** 根据波次数量和起始生命计算的难度提示。 */
  difficultyHint: string;
}

interface MapSelectorProps {
  content: GameContent;
  /** 玩家确认后以选中的 mapId 回调。 */
  onSelect: (mapId: string) => void;
  /** 当前选中的 mapId（用于高亮）。 */
  currentMapId?: string;
}

/**
 * Map selection panel shown on the main menu.
 *
 * Renders cards for each available map with name, wave count, and a
 * difficulty hint. Persists the choice to localStorage and notifies
 * the caller via `onSelect`.
 */
export function MapSelector({ content, onSelect, currentMapId }: MapSelectorProps) {
  const [selected, setSelected] = useState<string>(() => getStoredMapId());
  const { t, tEntity } = useI18n();

  // 当 prop 变化时同步内部状态；旧地图 ID 会收敛到玩家可用地图。
  useEffect(() => {
    if (currentMapId) {
      const mapId = getAvailableMapId(currentMapId);
      setSelected(mapId);
      if (mapId !== currentMapId) saveMapId(mapId);
    }
  }, [currentMapId]);

  const maps: MapInfo[] = Object.values(filterAvailableMaps(content.maps)).map((map) => {
    const waveCount = map.waves.length;
    const isEndless = map.isEndless === true;
    // 简单难度启发式：波次越少 + 生命越多 = 越容易。
    let hint: string;
    if (isEndless) {
      hint = t("map_hint_endless");
    } else if (waveCount <= 2) {
      hint = t("map_hint_short");
    } else if (waveCount <= 3) {
      hint = t("map_hint_medium");
    } else {
      hint = t("map_hint_long");
    }
    return {
      id: map.id,
      name: tEntity("map", map.id),
      totalWaves: waveCount,
      isEndless,
      difficultyHint: hint,
    };
  });

  const handleSelect = useCallback((mapId: string) => {
    if (AVAILABLE_MAP_IDS.has(mapId)) setSelected(mapId);
  }, []);

  const handleConfirm = useCallback(() => {
    const mapId = getAvailableMapId(selected);
    saveMapId(mapId);
    onSelect(mapId);
  }, [selected, onSelect]);

  return (
    <div className="map-selector-overlay" data-testid="map-selector">
      <div className="map-selector-panel" data-testid="map-selector-panel">
        <div className="map-selector-header">
          <h2 className="map-selector-title">{t("map_selector_title")}</h2>
        </div>

        <div className="map-selector-options">
          {maps.map((info) => (
            <button
              key={info.id}
              className={`map-card ${selected === info.id ? "active" : ""}`}
              data-testid={`map-${info.id}-card`}
              onClick={() => handleSelect(info.id)}
            >
              <span className="map-card-name" data-testid={`map-${info.id}-name`}>
                {tEntity("map", info.id)}
              </span>
              <span className="map-card-waves">
                {info.isEndless
                  ? t("map_endless_label")
                  : `${info.totalWaves} ${info.totalWaves !== 1 ? t("hud_wave") + "s" : t("hud_wave")}`}
              </span>
              <span className="map-card-hint">{info.difficultyHint}</span>
            </button>
          ))}
        </div>

        <div className="map-selector-actions">
          <button className="map-btn primary" data-testid="map-confirm-btn" onClick={handleConfirm}>
            {t("map_start_on", { name: tEntity("map", selected) })}
          </button>
        </div>
      </div>
    </div>
  );
}
